import { IUserDto } from "@kuika/kuika-cl-model";
import { AxiosResponse } from "axios";
import { useEffect, useState } from "react";
import { UserService } from "../../../services/user-service";

// Workspace people directory: maps a stable user id to that member's display name + profile-photo
// URL, so the board can store ids everywhere and resolve names/avatars at render time. Fetched
// once per session from GET /users/directory (a lean id/name/photo projection). Until it resolves,
// id→name falls back to a short id and avatars fall back to initials.

interface DirectoryListener {
  (): void;
}

export interface DirectoryMember {
  id: string;
  name: string;
  photoUrl?: string;
}

let membersById: Map<string, DirectoryMember> = new Map();
let sortedMembers: DirectoryMember[] = [];
let loaded: boolean = false;
let loading: boolean = false;
const listeners: Set<DirectoryListener> = new Set();

function emit(): void {
  for (const listener of listeners) listener();
}

function displayName(user: IUserDto): string {
  const fullName: string = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
  if (fullName) return fullName;
  return user.username ?? "";
}

export function subscribePeople(listener: DirectoryListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// Resolve a user id to its display name. Falls back to a short id fragment when the directory
// hasn't loaded or the user is unknown (deleted), so the UI never shows a bare GUID or blank.
export function displayNameOf(userId: string | null | undefined): string {
  if (!userId) return "";
  const member: DirectoryMember | undefined = membersById.get(userId);
  if (member) return member.name;
  return userId.length > 8 ? userId.slice(0, 8) : userId;
}

// React hook: load the directory and re-render the caller once it resolves (or changes). ANY
// component that renders names/avatars must use this — otherwise it computes `displayNameOf` once,
// before the directory loads, and gets stuck on the short-id fallback (the assignee-shows-a-GUID bug).
// Components rendered under a subscribing ancestor (the board page) re-render with it; standalone
// surfaces like the project hub's tabs have no such ancestor and must subscribe themselves.
export function usePeopleDirectory(): void {
  const [, bump] = useState<number>(0);
  useEffect(() => {
    const unsubscribe: () => void = subscribePeople(() => bump((value: number) => value + 1));
    void loadPeopleDirectory();
    return unsubscribe;
  }, []);
}

// Profile-photo URL for a user id, or undefined (caller falls back to initials).
export function avatarUrlOf(userId: string | null | undefined): string | undefined {
  if (!userId) return undefined;
  return membersById.get(userId)?.photoUrl;
}

// All workspace members (sorted by name), for assignee/member pickers — value = id, label = name.
export function getMembers(): DirectoryMember[] {
  return sortedMembers;
}

export async function loadPeopleDirectory(): Promise<void> {
  if (loaded || loading) return;
  loading = true;
  try {
    const response: AxiosResponse = await UserService.GetDomainUsersDirectory();
    const users: IUserDto[] = (response?.data as IUserDto[]) ?? [];
    const next: Map<string, DirectoryMember> = new Map<string, DirectoryMember>();
    for (const user of users) {
      if (!user.id) continue;
      const member: DirectoryMember = { id: user.id, name: displayName(user), photoUrl: user.profilePhotoUrl };
      next.set(user.id, member);
    }
    membersById = next;
    sortedMembers = Array.from(next.values()).sort((a: DirectoryMember, b: DirectoryMember) =>
      a.name.localeCompare(b.name)
    );
    loaded = true;
    emit();
  } catch {
    // UserService already routed the error through exceptionHandler.
  } finally {
    loading = false;
  }
}
